// ============================================
// 🔧 HELPERS PARA SUPABASE CON TIMEOUT Y RETRY
// ============================================

import { useState, useCallback } from 'react';

/**
 * Agrega timeout a una promesa
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  errorMessage: string = 'Operación tardó demasiado'
): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(`${errorMessage} (${ms}ms)`)), ms)
  );

  return Promise.race([promise, timeoutPromise]);
}

/**
 * Reintenta una función hasta N veces con delay exponencial
 */
export async function retry<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (i < retries - 1) {
        const waitTime = delay * Math.pow(2, i); // Backoff exponencial
        console.log(`⚠️ Intento ${i + 1}/${retries} falló. Reintentando en ${waitTime}ms...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }

  throw lastError!;
}

/**
 * Combina timeout + retry para consultas Supabase
 */
export async function safeFetch<T>(
  fn: () => Promise<T>,
  options: {
    timeout?: number;
    retries?: number;
    retryDelay?: number;
    errorMessage?: string;
  } = {}
): Promise<T> {
  const {
    timeout = 30000, // 30 segundos por defecto
    retries = 3,
    retryDelay = 1000,
    errorMessage = 'La operación tardó demasiado'
  } = options;

  return retry(
    () => withTimeout(fn(), timeout, errorMessage),
    retries,
    retryDelay
  );
}

/**
 * Hook para consultas seguras con loading y error
 */
export interface SafeQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: () => Promise<void>;
  reset: () => void;
}

export function useSafeQuery<T>(
  queryFn: () => Promise<T>,
  options: {
    timeout?: number;
    retries?: number;
    autoExecute?: boolean;
  } = {}
): SafeQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await safeFetch(queryFn, {
        timeout: options.timeout,
        retries: options.retries
      });
      setData(result);
    } catch (err: any) {
      const errorMsg = err?.message || 'Error desconocido al cargar datos';
      setError(errorMsg);
      setData(null);
      console.error('❌ Error en useSafeQuery:', err);
    } finally {
      setLoading(false);
    }
  }, [queryFn, options.timeout, options.retries]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, execute, reset };
}

